import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getUser } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { AlarmClock, Send, Clock, X } from "lucide-react";

/**
 * D10 — In-app sales-reminder popup.
 *
 * Behaviour:
 *   • Polls /api/notifications every 60s.
 *   • Renders a non-blocking toast-style card (bottom-right, no overlay) for the newest unread
 *     reminder of kind sales_reminder / sales_escalation that has NOT been snoozed yet.
 *   • Enter Now  → navigate to /promoter (sales entry) and mark the notification read.
 *   • Snooze 15  → hide for 15 minutes (localStorage keyed by notification id).
 *   • ESC key or the × button both snooze.
 *   • Escalation chain: promoter → supervisor + project lead notifications are all delivered
 *     to the recipient the same way (they are already scoped by user_id server-side).
 */

const SNOOZE_MIN = 15;
const snoozeKey = (id) => `sr_snz_${id}`;
const isSnoozed = (id) => {
  const until = parseInt(localStorage.getItem(snoozeKey(id)) || "0", 10);
  return until && until > Date.now();
};
const snoozeFor = (id, mins = SNOOZE_MIN) => {
  localStorage.setItem(snoozeKey(id), String(Date.now() + mins * 60 * 1000));
};

export function ReminderPopup() {
  // -------- All hooks live at the TOP, unconditionally --------
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const currentIdRef = useRef(null);
  const user = getUser();

  const fetchOne = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications", { params: { unread_only: true, limit: 20 } });
      const candidates = (data || []).filter(n =>
        (n.kind === "sales_reminder" || n.kind === "sales_escalation") && !isSnoozed(n.id)
      );
      setCurrent(candidates.length > 0 ? candidates[0] : null);
    } catch {
      // silent — polling can fail transiently, we'll try again on next tick
    }
  }, [user]);

  useEffect(() => {
    fetchOne();
    const t = setInterval(fetchOne, 60_000);
    return () => clearInterval(t);
  }, [fetchOne]);

  // Keep a ref of the currently-shown notification id so the ESC handler (mounted once) can act on it.
  useEffect(() => {
    currentIdRef.current = current?.id || null;
  }, [current]);

  // ESC key snoozes so the popup can never lock the user in. Mounted once; no-op when nothing is shown.
  useEffect(() => {
    const h = (e) => {
      if (e.key !== "Escape") return;
      const id = currentIdRef.current;
      if (!id) return;
      snoozeFor(id, SNOOZE_MIN);
      setCurrent(null);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // -------- Handlers use `current` inside the body, no hooks after this point --------
  const snooze = () => {
    if (!current) return;
    snoozeFor(current.id, SNOOZE_MIN);
    toast.info(`Snoozed for ${SNOOZE_MIN} minutes`);
    setCurrent(null);
  };

  const enterNow = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await api.post(`/notifications/${current.id}/read`);
      toast.success("Opening entry screen…");
      if (user?.role === "Promoter") navigate("/promoter");
      else navigate("/admin/late-entries");
    } catch { /* still close */ }
    finally { setBusy(false); setCurrent(null); }
  };

  if (!current) return null;

  return (
    <div
      role="alertdialog"
      aria-label="Sales entry reminder"
      data-testid="reminder-popup"
      className="fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 p-4 animate-in slide-in-from-bottom-4"
    >
      <button
        onClick={snooze}
        aria-label="Dismiss (snooze 15 min)"
        data-testid="reminder-close-btn"
        className="absolute top-2 right-2 h-8 w-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-800 hover:bg-gray-100"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-2 mb-2">
        <AlarmClock className="h-5 w-5 text-brand-red" />
        <div className="font-display font-bold text-sm">
          {current.kind === "sales_escalation" ? "Escalation" : `Reminder · ${current.slot} IST`}
        </div>
      </div>
      <div className="text-sm text-gray-700 mb-4" data-testid="reminder-message">{current.message}</div>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={snooze} data-testid="reminder-snooze-btn">
          <Clock className="h-4 w-4 mr-1.5" /> Snooze 15 min
        </Button>
        <Button size="sm" className="bg-brand-red hover:bg-[#B31721]" onClick={enterNow} disabled={busy} data-testid="reminder-enter-now-btn">
          <Send className="h-4 w-4 mr-1.5" /> Enter Now
        </Button>
      </div>
    </div>
  );
}
